import { getReferenceId } from '@quromedical/fhir-common';
import type { Group, GroupCreateRequest } from '@quromedical/models';
import {
  Alert,
  Skeleton,
  SkeletonProvider,
  Snackbar,
  Visible,
} from 'design-system';
import { logger } from 'helpers';
import { useFiniteState } from 'hooks/useFiniteState';
import type React from 'react';
import { useCallback } from 'react';
import { strings } from 'strings';

import { Card } from '../cards';
import { Form, type OrganizationFetcher } from './Form';

interface GroupFormCardProps {
  canEdit: boolean;
  data: Partial<Group>;
  revalidate: () => void;
  onSubmit: (group: GroupCreateRequest) => Promise<void>;
  organizationFetcher: OrganizationFetcher;
}

type FormState = 'initial' | 'editing' | 'submitting' | 'error';

const mapGroupToFormGroup = (
  group: Partial<Group> = {},
): Partial<GroupCreateRequest> => ({
  ...group,
  organization: getReferenceId(group.organization),
});

export const GroupFormCard: React.FC<GroupFormCardProps> = ({
  data,
  revalidate,
  canEdit,
  onSubmit,
  organizationFetcher,
}) => {
  const state = useFiniteState<FormState>('initial');

  const handleSubmit = useCallback(
    async (result: GroupCreateRequest) => {
      state.set('submitting');
      try {
        await onSubmit(result);
        state.set('initial');
        revalidate();
        return {};
      } catch (error) {
        state.set('error');
        logger.error(error);
        return { error };
      }
    },
    [revalidate, onSubmit, state],
  );
  const createData = mapGroupToFormGroup(data);

  return (
    <>
      <SkeletonProvider loading={state.is('submitting')}>
        <Skeleton>
          <Visible if={state.isNot('editing')}>
            <Card
              data={data}
              canEdit={canEdit}
              onEditPress={state.next('editing')}
            />
          </Visible>

          <Visible if={state.is('editing')}>
            <Form
              data={createData}
              handleCancel={state.next('initial')}
              handleSubmit={handleSubmit}
              showSecondarySubmitButton
              organizationFetcher={organizationFetcher}
              initialOrganization={data.organization}
              title={strings.CardTitleGroupEdit}
            />
          </Visible>
        </Skeleton>
      </SkeletonProvider>

      <Snackbar onClose={state.next('editing')} isOpen={state.is('error')}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={state.next('editing')}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    </>
  );
};
